// Library imports
import React, { useState, useEffect } from 'react';
import { Joyride, ACTIONS, STATUS } from 'react-joyride';
import { MDBBtn, MDBIcon } from 'mdb-react-ui-kit';
import { useDispatch, useSelector } from 'react-redux';
// API imports
import { updateUserProfile } from '../../api/user';
// Action imports
import { setProfile } from '../../store/slices/userSlice';

const TOUR_ID = 'game-cards-web';

const stepContent = (icon, title, body) => (
    <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '10px' }}>
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 30,
                    height: 30,
                    borderRadius: '8px',
                    background: '#eff6ff',
                    flexShrink: 0,
                }}
            >
                {icon}
            </span>
            <span style={{ fontSize: '0.875rem', fontWeight: '700', color: '#0f172a', letterSpacing: '-0.01em' }}>{title}</span>
        </div>
        <div style={{ fontSize: '0.84rem', color: '#475569', lineHeight: '1.55' }}>{body}</div>
    </div>
);

const chip = (label, colors) => (
    <span
        key={label}
        style={{
            display: 'inline-block',
            borderRadius: '6px',
            padding: '2px 9px',
            fontSize: '0.76rem',
            fontWeight: '600',
            background: (colors && colors.bg) || '#eff6ff',
            color: (colors && colors.text) || '#1d4ed8',
            border: '1px solid ' + ((colors && colors.border) || '#bfdbfe'),
        }}
    >
        {label}
    </span>
);

// v3: skipBeacon replaces disableBeacon; buttons array replaces showSkipButton
const STEP_OPTIONS = {
    skipBeacon: true,
    buttons: ['back', 'primary', 'skip'],
};

const TOUR_STEPS = [
    {
        ...STEP_OPTIONS,
        target: '[data-tour="game-date-filter"]',
        content: stepContent(
            <MDBIcon fas icon="calendar-alt" style={{ fontSize: '0.85rem', color: '#3b82f6' }} />,
            'Game Date',
            "Browse games by date — view today's matchups, upcoming games, or revisit past results.",
        ),
        placement: 'bottom',
    },
    {
        ...STEP_OPTIONS,
        target: '[data-tour="search-teams"]',
        content: stepContent(
            <MDBIcon fas icon="search" style={{ fontSize: '0.85rem', color: '#3b82f6' }} />,
            'Search Teams',
            'Type any team name to instantly filter the game cards to that matchup.',
        ),
        placement: 'bottom',
    },
    {
        ...STEP_OPTIONS,
        target: '[data-tour="filter-games"]',
        content: stepContent(
            <MDBIcon fas icon="filter" style={{ fontSize: '0.85rem', color: '#3b82f6' }} />,
            'Filter Games',
            <>
                <span style={{ display: 'block', marginBottom: '8px' }}>Show only games matching a status:</span>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {chip('Upcoming', { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' })}
                    {chip('Live', { bg: '#fef2f2', text: '#dc2626', border: '#fecaca' })}
                </div>
            </>,
        ),
        placement: 'bottom',
    },
    {
        ...STEP_OPTIONS,
        target: '[data-tour="sort-games"]',
        content: stepContent(
            <MDBIcon fas icon="sort-amount-down" style={{ fontSize: '0.85rem', color: '#3b82f6' }} />,
            'Sort Games',
            <>
                Sort by <strong>Time</strong> for chronological order, or <strong>PW Rating</strong> to surface the highest-rated predictions first.
            </>,
        ),
        placement: 'bottom',
    },
    {
        ...STEP_OPTIONS,
        target: '[data-tour="game-card"]',
        content: stepContent(
            <MDBIcon fas icon="layer-group" style={{ fontSize: '0.85rem', color: '#3b82f6' }} />,
            'Game Card',
            'Each card is a full research hub for one matchup. Use the toolbar at the bottom to switch between data views.',
        ),
        placement: 'top',
    },
    {
        ...STEP_OPTIONS,
        buttons: ['back', 'primary'],
        target: '[data-tour="game-toolbar"]',
        content: stepContent(
            <MDBIcon fas icon="th-large" style={{ fontSize: '0.85rem', color: '#3b82f6' }} />,
            'Game Card Bottom Bar',
            <>
                <span style={{ display: 'block', marginBottom: '8px' }}>Tap any tab to dive into a specific angle:</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    {['Power', 'Lines', 'Public', 'Factors', 'Totals', 'Injuries', 'Rosters', 'Trends', 'Schedules'].map(l => chip(l))}
                    {chip('Prediction', { bg: '#fffbeb', text: '#b45309', border: '#fde68a' })}
                </div>
            </>,
        ),
        placement: 'top',
    },
];

// Fallback step shown when no game cards are in the DOM yet
const noGamesStep = {
    ...STEP_OPTIONS,
    buttons: ['back', 'primary'],
    target: '[data-tour="search-teams"]',
    placement: 'bottom',
    content: stepContent(
        <MDBIcon fas icon="clock" style={{ fontSize: '0.85rem', color: '#3b82f6' }} />,
        'No Games Scheduled Yet',
        'There are no games scheduled for this sport right now. When games are available each card becomes a full research hub with predictions, Proven Line, PW Rating, and ten data windows.',
    ),
};

const getActiveSteps = (hasGames) => {
    // Always include search/filter/sort steps (targets are always in DOM).
    // Game-card steps are only safe when cards are rendered.
    // If a specific target is somehow absent, skip that step gracefully.
    const candidates = hasGames ? TOUR_STEPS : [
        ...TOUR_STEPS.slice(0, 4),
        noGamesStep,
    ];
    return candidates.filter(step => {
        try {
            return Boolean(document.querySelector(step.target));
        } catch {
            return false;
        }
    });
};

const GameCardsTour = ({ hasGames = true }) => {
    const [run, setRun] = useState(false);
    const [steps, setSteps] = useState(TOUR_STEPS);
    const dispatch = useDispatch();
    const { profile, profileLoaded } = useSelector(state => state.user);
    // A missing profile is treated as a first-time user, so the tour auto-starts.
    const tourCompleted = Array.isArray(profile?.preferences?.toursCompleted) && profile.preferences.toursCompleted.includes(TOUR_ID);

    useEffect(() => {
        if (!profileLoaded || tourCompleted) {
            return;
        }

        const timer = setTimeout(() => {
            setSteps(getActiveSteps(hasGames));
            setRun(true);
        }, 1200);

        return () => clearTimeout(timer);
    }, [profileLoaded, tourCompleted, hasGames]);

    // v3: prop is onEvent(data, controls) — not callback
    const handleEvent = (data) => {
        const { action, status } = data;

        if (
            status === STATUS.FINISHED ||
            status === STATUS.SKIPPED ||
            action === ACTIONS.CLOSE ||
            action === ACTIONS.SKIP
        ) {
            setRun(false);

            // Save to database (fire-and-forget)
            updateUserProfile({ tourCompleted: TOUR_ID })
                .then(updated => {
                    if (updated) {
                        dispatch(setProfile(updated));
                    }
                })
                .catch(error => console.error('Error saving tour completion:', error));

            localStorage.setItem('gameCardsTourCompletedWeb', 'true');

            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 100);
        }
    };

    const startTour = () => {
        setRun(false);
        setTimeout(() => {
            setSteps(getActiveSteps(hasGames));
            setRun(true);
        }, 100);
    };

    return (
        <>
            <MDBBtn
                floating
                size="md"
                onClick={startTour}
                style={{
                    position: 'fixed',
                    left: '2rem',
                    bottom: '2rem',
                    zIndex: 1000,
                    background: '#3b82f6',
                    color: '#fff',
                    border: 0,
                    boxShadow: '0 4px 12px rgba(0,123,255,0.4)',
                }}
                title="Show Interactive Tour"
            >
                <MDBIcon fas icon="question-circle" />
            </MDBBtn>
            <Joyride
                steps={steps}
                run={run && steps.length > 0}
                continuous
                onEvent={handleEvent}
                styles={{
                    options: {
                        primaryColor: '#3b82f6',
                        textColor: '#1e293b',
                        backgroundColor: '#ffffff',
                        arrowColor: '#ffffff',
                        zIndex: 10000,
                    },
                    tooltip: {
                        padding: '20px',
                        borderRadius: '14px',
                        boxShadow: '0 8px 40px rgba(15,23,42,0.16), 0 2px 8px rgba(15,23,42,0.08)',
                        maxWidth: '300px',
                        borderTop: '3px solid #3b82f6',
                    },
                    tooltipContent: {
                        padding: '0',
                    },
                    tooltipFooter: {
                        padding: '10px 0 0',
                        marginTop: '10px',
                        borderTop: '1px solid #f1f5f9',
                    },
                    buttonNext: {
                        backgroundColor: '#3b82f6',
                        borderRadius: '20px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        padding: '6px 18px',
                        boxShadow: '0 2px 6px rgba(59,130,246,0.35)',
                        border: 'none',
                    },
                    buttonBack: {
                        color: '#94a3b8',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        background: 'none',
                        border: 'none',
                        padding: '6px 10px',
                    },
                    buttonSkip: {
                        color: '#94a3b8',
                        fontSize: '0.75rem',
                        background: 'none',
                        border: 'none',
                    },
                    spotlight: {
                        borderRadius: '8px',
                    },
                }}
                locale={{
                    back: 'Previous',
                    close: 'Close',
                    last: 'Finish',
                    next: 'Next',
                    skip: 'Skip Tour',
                }}
            />
        </>
    );
};

export default GameCardsTour;
